import { useEffect, useState } from 'react';
import { Loader, CheckCircle, XCircle } from 'lucide-react';

interface QRLoginProps {
  onLoginSuccess: (userData: any) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initDataUnsafe?: {
          user?: {
            id: number;
            first_name?: string;
            last_name?: string;
            username?: string;
          };
        };
      };
    };
  }
}

function QRLogin({ onLoginSuccess }: QRLoginProps) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying login...');

  useEffect(() => {
    const verifyLogin = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (!token) {
          setStatus('error');
          setMessage('Invalid login link - no token provided');
          return;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        const response = await fetch(
          `${supabaseUrl}/functions/v1/qr-login?token=${token}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${supabaseKey}`,
            },
          }
        );

        const result = await response.json();

        if (result.success && result.user) {
          const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

          if (telegramUser && telegramUser.id.toString() !== result.user.telegram_id) {
            setStatus('error');
            setMessage('Security Error: This login link is not for your account. Please request your own login link.');
            return;
          }

          setStatus('success');
          setMessage('Login successful! Redirecting...');

          setTimeout(() => {
            onLoginSuccess(result.user);
          }, 1500);
        } else {
          setStatus('error');
          setMessage(result.error || 'Login failed');
        }
      } catch (error) {
        console.error('QR Login error:', error);
        setStatus('error');
        setMessage('An error occurred during login');
      }
    };

    verifyLogin();
  }, [onLoginSuccess]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center">
          <div className="mb-6">
            {status === 'loading' && (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full">
                <Loader className="w-10 h-10 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
            )}
            {status === 'error' && (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900 rounded-full">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {status === 'loading' && 'Verifying Login'}
            {status === 'success' && 'Login Successful'}
            {status === 'error' && 'Login Failed'}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {message}
          </p>

          {status === 'error' && (
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Return Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default QRLogin;
